// Detail Dokumen.
//   doc_header        divided section  title, status, meta, tools (Edit, Unduh .md, Cetak)
//   disposisi         card             beside the header from 1024px: Dari, Sejak, Instruksi or Catatan revisi,
//                                      Menunggu, and the allowed actions (core.list_text_pairs + core.button_row)
//   decision_bar      docked toolbar   below 640px, pinned above the dock, its height reserved (core.toolbar_docked)
//   ai_banner         notice           AI draft, human review required
//   content_pane      card             rendered Markdown; edit mode with Tulis and Pratinjau tabs (core.tabs_secondary)
//   aside, from 1024px beside the content, below it on smaller screens:
//     regenerate_panel  card             Buat ulang with extra instructions
//     versions_panel    rows             versions, open an old one in a dialog
//     riwayat_panel     divided section  events (core.list_text)
//     citations_panel   plain spacing    every knowledge section the AI used
// The revision note moved into the Disposisi card (critic round 1), so it is not repeated as a notice.
import { useEffect, useMemo, useState, type FormEvent } from "react";
import * as API from "../api";
import { useUser } from "../auth";
import { docTypeLabel, loadCatalog, subLabel } from "../lib/catalog";
import { citationLabel } from "../lib/citations";
import { dispositionOf } from "../lib/disposition";
import { formatDateTime, formatRelative } from "../lib/format";
import {
  ACTION_ORDER,
  actionConfirm,
  actionLabel,
  EVENT_LABEL,
  FORWARD_ACTIONS,
  isTransitionAction,
  STATUS_LABEL,
  TAHAP_LABEL,
} from "../lib/labels";
import { Markdown } from "../lib/markdown";
import {
  Badge,
  Dialog,
  EmptyState,
  Field,
  fieldAria,
  Notice,
  PageHeader,
  SectionHead,
  SkeletonRows,
  StatusBadge,
  Tabs,
  useAsync,
  useTitle,
} from "../ui/components";
import { GenerationError, GenerationProgress, useElapsed } from "../ui/generation";
import { Glyph } from "../ui/icons";

export function DocumentDetailScreen({ id }: { id: string }) {
  const user = useUser();
  const doc = useAsync(() => API.getDocument(id), [id]);
  const catalog = useAsync(() => loadCatalog(user.id).catch(() => [] as API.CatalogTahap[]), [user.id]);
  useTitle(doc.data?.title ?? "Dokumen");

  if (doc.loading && !doc.data) {
    return (
      <>
        <PageHeader title="Memuat dokumen" back={{ href: "/dokumen", label: "Dokumen" }} titleSize="document" />
        <SkeletonRows count={6} label="Memuat dokumen" />
      </>
    );
  }
  if (doc.error || !doc.data) {
    const status = doc.error instanceof API.ApiError ? doc.error.status : 0;
    return (
      <>
        <PageHeader
          title={status === 404 ? "Dokumen tidak ditemukan" : status === 403 ? "Akses ditolak" : "Dokumen belum dapat dimuat"}
          back={{ href: "/dokumen", label: "Dokumen" }}
          titleSize="document"
        />
        <Notice
          tone={status === 404 ? "info" : "danger"}
          title={status === 404 ? "Dokumen ini tidak ada atau sudah tidak tersedia" : status === 403 ? "Anda tidak memiliki akses ke dokumen ini" : "Terjadi kesalahan"}
          action={
            status === 404 || status === 403 ? (
              <a className="btn btn-secondary" href="/dokumen">
                Kembali ke Dokumen
              </a>
            ) : (
              <button type="button" className="btn btn-secondary" onClick={doc.reload}>
                Muat ulang
              </button>
            )
          }
        >
          <p>{API.errorMessage(doc.error)}</p>
        </Notice>
      </>
    );
  }

  return <DocumentView doc={doc.data} catalog={catalog.data} onChange={doc.set} penyedia={user.role === "penyedia"} />;
}

/** The draft notice: a government draft waits for an official; a Penyedia checks its own against the dokumen pemilihan. */
export function draftNotice(status: API.DocumentDetail["status"], penyedia: boolean): { title: string; body: string } {
  if (status === "final") {
    return {
      title: "Final, disusun dengan bantuan Pluto",
      body: penyedia
        ? "Isi awal disusun Pluto dan sudah Anda tandai final. Ubah kembali bila dokumen pemilihan atau kontrak berubah."
        : "Isi awal disusun Pluto dan sudah ditetapkan oleh pejabat berwenang melalui alur review.",
    };
  }
  return {
    title: "Draf rekomendasi Pluto",
    body: penyedia
      ? "Isi disusun otomatis. Periksa dan sesuaikan dengan dokumen pemilihan sebelum Anda gunakan atau unggah."
      : "Isi disusun otomatis dan wajib direview serta diputuskan oleh pejabat berwenang sebelum digunakan.",
  };
}

function DocumentView({
  doc,
  catalog,
  onChange,
  penyedia = false,
}: {
  doc: API.DocumentDetail;
  catalog: API.CatalogTahap[] | null;
  onChange: (d: API.DocumentDetail) => void;
  penyedia?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [pending, setPending] = useState<API.TransitionAction | null>(null);
  const [exportState, setExportState] = useState<{ busy: boolean; error: string | null }>({ busy: false, error: null });
  const [flash, setFlash] = useState<string | null>(null);
  const [regenerating, setRegenerating] = useState(false);

  const meta = [TAHAP_LABEL[doc.tahap], docTypeLabel(catalog, doc.docType), subLabel(catalog, doc.docType, doc.subCategory)].join(", ");

  async function download() {
    setExportState({ busy: true, error: null });
    try {
      const text = await API.exportDocument(doc.id);
      const blob = new Blob([text], { type: "text/markdown;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${slug(doc.title) || "dokumen"}-v${doc.currentVersion}.md`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setExportState({ busy: false, error: null });
    } catch (err) {
      setExportState({ busy: false, error: API.errorMessage(err) });
    }
  }

  return (
    <>
      <div className="doc-top divided-below">
        <div className="doc-head">
          <PageHeader
            title={doc.title}
            back={{ href: "/dokumen", label: "Dokumen" }}
            titleSize="document"
            lead={
              <span className="doc-meta">
                <StatusBadge status={doc.status} />
                <span>{meta}</span>
              </span>
            }
          />
          <p className="doc-facts">
            <span>
              Versi <span className="num">{doc.currentVersion}</span>
            </span>
            <span>Dibuat {doc.createdByName}</span>
            <span>Diperbarui {formatDateTime(doc.updatedAt)}</span>
          </p>
          <DocTools
            canEdit={doc.canEdit}
            editing={editing || regenerating}
            onEdit={() => {
              setFlash(null);
              setEditing(true);
            }}
            onDownload={download}
            downloadBusy={exportState.busy}
          />
          {exportState.error ? (
            <Notice tone="danger" title="Berkas belum dapat diunduh">
              <p>{exportState.error}</p>
            </Notice>
          ) : null}
        </div>
        <DispositionCard doc={doc} busy={editing || regenerating} onAction={setPending} penyedia={penyedia} />
      </div>

      <Notice tone="info" title={draftNotice(doc.status, penyedia).title} className="ai-banner">
        <p>{draftNotice(doc.status, penyedia).body}</p>
      </Notice>

      {flash ? (
        <Notice tone="success" title={flash} className="no-print">
          <p>Riwayat dan versi dokumen sudah diperbarui.</p>
        </Notice>
      ) : null}

      <div className="doc-layout">
        <div className="doc-main">
          {editing ? (
            <EditPane
              doc={doc}
              onCancel={() => setEditing(false)}
              onSaved={(d) => {
                onChange(d);
                setEditing(false);
                setFlash(`Perubahan tersimpan sebagai versi ${d.currentVersion}`);
              }}
            />
          ) : (
            <article className="card doc-card" aria-label="Isi dokumen">
              {doc.content.trim() ? (
                <Markdown source={doc.content} className="doc-content" />
              ) : (
                <EmptyState icon="documentText" title="Dokumen masih kosong" body="Susun ulang dengan Pluto atau isi secara manual melalui Edit." />
              )}
            </article>
          )}
        </div>
        <aside className="doc-aside no-print" aria-label="Keterangan dokumen">
          {doc.canEdit && !editing ? (
            <RegeneratePanel
              doc={doc}
              onBusy={setRegenerating}
              onDone={(d) => {
                onChange(d);
                setFlash(`Draf baru disusun Pluto sebagai versi ${d.currentVersion}`);
              }}
            />
          ) : null}
          <VersionsPanel doc={doc} />
          <HistoryPanel events={doc.events} />
          <CitationsPanel citations={doc.citations} />
        </aside>
      </div>

      {pending ? (
        <ActionDialog
          doc={doc}
          penyedia={penyedia}
          action={pending}
          onClose={() => setPending(null)}
          onDone={(d) => {
            onChange(d);
            setPending(null);
            setFlash(`Status sekarang ${STATUS_LABEL[d.status]}`);
          }}
        />
      ) : null}

      {!editing && !regenerating && decisionCount(doc) > 0 ? (
        <div className="decision-bar no-print" data-overlay="" role="toolbar" aria-label="Keputusan dokumen">
          <DecisionButtons doc={doc} onAction={setPending} penyedia={penyedia} />
        </div>
      ) : null}
    </>
  );
}

function slug(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/[\s_]+/g, "-")
    .slice(0, 80);
}

// ---------------------------------------------------------------------------
// Workflow actions: only what allowedActions says; one primary at most
// ---------------------------------------------------------------------------
export function orderedActions(allowed: string[]): API.TransitionAction[] {
  return ACTION_ORDER.filter((a) => allowed.includes(a));
}

export function decisionCount(doc: Pick<API.DocumentDetail, "allowedActions">): number {
  return orderedActions(doc.allowedActions.filter(isTransitionAction)).length;
}

/** The allowed workflow actions, one primary (the forward action) at most. */
export function DecisionButtons({
  doc,
  onAction,
  penyedia = false,
}: {
  doc: Pick<API.DocumentDetail, "allowedActions">;
  onAction: (a: API.TransitionAction) => void;
  penyedia?: boolean;
}) {
  const actions = orderedActions(doc.allowedActions.filter(isTransitionAction));
  const primary = actions.find((a) => FORWARD_ACTIONS.includes(a)) ?? null;
  return (
    <>
      {actions.map((a) => (
        <button key={a} type="button" className={a === primary ? "btn" : "btn btn-secondary"} data-action={a} onClick={() => onAction(a)}>
          {a === "submit" ? <Glyph name="send" /> : a === "approve" || a === "finalize" ? <Glyph name="tickCircle" /> : null}
          <span>{actionLabel(a, penyedia)}</span>
        </button>
      ))}
    </>
  );
}

/** Edit, Unduh .md, Cetak: the tools, secondary to the decision. */
export function DocTools({
  canEdit,
  editing,
  onEdit,
  onDownload,
  downloadBusy,
}: {
  canEdit: boolean;
  editing: boolean;
  onEdit: () => void;
  onDownload: () => void;
  downloadBusy: boolean;
}) {
  return (
    <div className="button-row doc-tools no-print" role="group" aria-label="Alat dokumen">
      {canEdit && !editing ? (
        <button type="button" className="btn btn-secondary" onClick={onEdit} data-action="edit">
          <Glyph name="edit2" />
          <span>Edit</span>
        </button>
      ) : null}
      <button type="button" className="btn btn-ghost" onClick={onDownload} aria-busy={downloadBusy || undefined}>
        <Glyph name="documentDownload" />
        <span>Unduh .md</span>
      </button>
      <button type="button" className="btn btn-ghost" onClick={() => window.print()}>
        <Glyph name="printer" />
        <span>Cetak</span>
      </button>
    </div>
  );
}

/** The decision buttons and the tools together (kept for callers that want one row). */
export function WorkflowActions({
  doc,
  editing,
  onAction,
  onEdit,
  onDownload,
  downloadBusy,
}: {
  doc: Pick<API.DocumentDetail, "allowedActions" | "canEdit" | "status">;
  editing: boolean;
  onAction: (a: API.TransitionAction) => void;
  onEdit: () => void;
  onDownload: () => void;
  downloadBusy: boolean;
}) {
  return (
    <div className="button-row doc-actions no-print" role="group" aria-label="Tindakan dokumen">
      {editing ? null : <DecisionButtons doc={doc} onAction={onAction} />}
      <DocTools canEdit={doc.canEdit} editing={editing} onEdit={onEdit} onDownload={onDownload} downloadBusy={downloadBusy} />
    </div>
  );
}

/** The lembar disposisi at the decision point: from whom, since when, the instruction, whose turn, the actions. */
export function DispositionCard({
  doc,
  busy,
  onAction,
  penyedia = false,
}: {
  doc: API.DocumentDetail;
  busy: boolean;
  onAction: (a: API.TransitionAction) => void;
  /** A Penyedia's own document: no disposition chain, so the card is its status. */
  penyedia?: boolean;
}) {
  const d = dispositionOf(doc, penyedia);
  const count = decisionCount(doc);
  return (
    <section className="card disposition no-print" aria-labelledby="disp-title">
      <h2 id="disp-title" className="card-subtitle">
        {penyedia ? "Status dokumen" : "Disposisi"}
      </h2>
      <dl className="disposition-facts">
        <div className="fact">
          <dt>Dari</dt>
          <dd>{d.from}</dd>
        </div>
        <div className="fact">
          <dt>Sejak</dt>
          <dd title={formatDateTime(d.since)}>{formatRelative(d.since)}</dd>
        </div>
        <div className="fact" data-wide="">
          <dt>{doc.status === "revisi" ? "Catatan revisi" : "Instruksi"}</dt>
          <dd className="pre-line">{d.instruction}</dd>
        </div>
        <div className="fact" data-wide="">
          <dt>Menunggu</dt>
          <dd>{d.waitingOn}</dd>
        </div>
      </dl>
      {busy ? (
        <p className="quiet-line">
          <Glyph name="infoCircle" />
          <span>Selesaikan perubahan isi untuk melanjutkan alur.</span>
        </p>
      ) : count > 0 ? (
        <div className="button-row disposition-actions" role="group" aria-label="Tindakan dokumen">
          <DecisionButtons doc={doc} onAction={onAction} penyedia={penyedia} />
        </div>
      ) : (
        <p className="quiet-line">
          <Glyph name="infoCircle" />
          <span>{doc.status === "final" ? "Alur dokumen ini sudah selesai." : "Tidak ada tindakan untuk Anda pada status ini."}</span>
        </p>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Transition dialog (note required for Minta Revisi)
// ---------------------------------------------------------------------------
export function ActionDialog({
  doc,
  action,
  onClose,
  onDone,
  penyedia = false,
}: {
  doc: API.DocumentDetail;
  action: API.TransitionAction;
  onClose: () => void;
  onDone: (d: API.DocumentDetail) => void;
  penyedia?: boolean;
}) {
  const copy = actionConfirm(action, penyedia);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (copy.noteRequired && !note.trim()) {
      setError("Catatan revisi wajib diisi.");
      document.getElementById("action-note")?.focus();
      return;
    }
    setError(null);
    setFailure(null);
    setBusy(true);
    try {
      const d = await API.transitionDocument(doc.id, action, note.trim() || undefined);
      onDone(d);
    } catch (err) {
      setFailure(API.errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      title={copy.title}
      description={copy.body}
      onClose={onClose}
      busy={busy}
      size="sm"
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Batal
          </button>
          <button type="submit" form="action-form" className="btn" aria-busy={busy || undefined}>
            {copy.confirm}
          </button>
        </>
      }
    >
      <form id="action-form" className="form-stack" noValidate onSubmit={submit}>
        {failure ? (
          <Notice tone="danger" title="Tindakan belum berhasil">
            <p>{failure}</p>
          </Notice>
        ) : null}
        <Field id="action-note" label={copy.noteRequired ? "Catatan revisi" : "Catatan (opsional)"} required={copy.noteRequired} error={error}>
          <textarea {...fieldAria("action-note", error)} data-autofocus rows={4} value={note} onChange={(e) => setNote(e.currentTarget.value)} maxLength={2000} required={copy.noteRequired} />
        </Field>
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Edit mode: Markdown textarea with Tulis and Pratinjau
// ---------------------------------------------------------------------------
function EditPane({ doc, onCancel, onSaved }: { doc: API.DocumentDetail; onCancel: () => void; onSaved: (d: API.DocumentDetail) => void }) {
  const [title, setTitle] = useState(doc.title);
  const [content, setContent] = useState(doc.content);
  const [tab, setTab] = useState("tulis");
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [titleError, setTitleError] = useState<string | null>(null);
  const dirty = title !== doc.title || content !== doc.content;

  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  async function save(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) {
      setTitleError("Judul wajib diisi.");
      return;
    }
    setTitleError(null);
    setBusy(true);
    setFailure(null);
    try {
      const d = await API.saveDocument(doc.id, content, title.trim() !== doc.title ? title.trim() : undefined);
      onSaved(d);
    } catch (err) {
      setFailure(API.errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <form className="card doc-card edit-pane" onSubmit={save} noValidate aria-label="Edit dokumen">
      {failure ? (
        <Notice tone="danger" title="Perubahan belum tersimpan">
          <p>{failure}</p>
        </Notice>
      ) : null}
      <div className="field-row" data-cols="1">
        <Field id="edit-title" label="Judul" required error={titleError}>
          <input {...fieldAria("edit-title", titleError)} value={title} onChange={(e) => setTitle(e.currentTarget.value)} maxLength={200} />
        </Field>
      </div>
      <Tabs
        label="Mode edit"
        idBase="edit"
        items={[
          { id: "tulis", label: "Tulis" },
          { id: "pratinjau", label: "Pratinjau" },
        ]}
        value={tab}
        onChange={setTab}
      />
      <div id="edit-panel" role="tabpanel" aria-labelledby={`edit-tab-${tab}`} className="tab-panel">
        {tab === "tulis" ? (
          <div className="field">
            <label htmlFor="edit-content">Isi dalam format Markdown</label>
            <textarea id="edit-content" className="md-editor" value={content} onChange={(e) => setContent(e.currentTarget.value)} spellCheck />
            <p className="field-hint">Gunakan # untuk judul bagian, - untuk daftar, dan | untuk tabel.</p>
          </div>
        ) : content.trim() ? (
          <Markdown source={content} className="doc-content" />
        ) : (
          <p className="quiet-line">
            <Glyph name="infoCircle" />
            <span>Belum ada isi untuk dipratinjau.</span>
          </p>
        )}
      </div>
      <div className="button-row">
        <button type="submit" className="btn" aria-busy={busy || undefined} disabled={!dirty && !busy}>
          Simpan sebagai versi baru
        </button>
        <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={busy}>
          Batal
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Regenerate with AI
// ---------------------------------------------------------------------------
function RegeneratePanel({ doc, onDone, onBusy }: { doc: API.DocumentDetail; onDone: (d: API.DocumentDetail) => void; onBusy: (b: boolean) => void }) {
  const [instructions, setInstructions] = useState("");
  const [state, setState] = useState<{ kind: "idle" } | { kind: "busy" } | { kind: "failed"; error: unknown }>({ kind: "idle" });
  const seconds = useElapsed(state.kind === "busy");

  async function run() {
    setState({ kind: "busy" });
    onBusy(true);
    try {
      const d = await API.regenerateDocument(doc.id, instructions.trim() || undefined);
      setInstructions("");
      setState({ kind: "idle" });
      onDone(d);
    } catch (error) {
      setState({ kind: "failed", error });
    } finally {
      onBusy(false);
    }
  }

  return (
    <section className="card aside-card" aria-labelledby="regen-title">
      <h2 id="regen-title" className="card-subtitle">
        Susun ulang dengan Pluto
      </h2>
      {state.kind === "busy" ? (
        <GenerationProgress what="baru" seconds={seconds} />
      ) : state.kind === "failed" ? (
        <GenerationError
          error={state.error}
          onRetry={() => void run()}
          secondary={
            <button type="button" className="btn btn-ghost" onClick={() => setState({ kind: "idle" })}>
              Batal
            </button>
          }
        />
      ) : (
        <form
          className="form-stack"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void run();
          }}
        >
          <Field id="regen-instructions" label="Instruksi tambahan" hint="Versi sekarang tetap tersimpan di daftar versi.">
            <textarea {...fieldAria("regen-instructions")} rows={3} value={instructions} onChange={(e) => setInstructions(e.currentTarget.value)} maxLength={2000} placeholder="Contoh: perjelas jadwal pelaksanaan" />
          </Field>
          <button type="submit" className="btn btn-secondary btn-block">
            <Glyph name="refresh" />
            <span>Buat ulang draf</span>
          </button>
        </form>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Versions, riwayat, citations
// ---------------------------------------------------------------------------
function VersionsPanel({ doc }: { doc: API.DocumentDetail }) {
  const [open, setOpen] = useState<number | null>(null);
  const versions = useMemo(() => [...doc.versions].sort((a, b) => b.version - a.version), [doc.versions]);
  return (
    <section className="aside-section" aria-labelledby="versions-title">
      <SectionHead id="versions-title" title="Versi" />
      <ul className="rows compact-rows">
        {versions.map((v) => (
          <li key={v.version} className="row">
            <span className="row-main">
              <span className="row-title">
                Versi <span className="num">{v.version}</span>, {v.source === "ai" ? "Pluto" : "manual"}
              </span>
              <span className="row-meta">
                {v.createdByName}, {formatDateTime(v.createdAt)}
              </span>
            </span>
            {v.version === doc.currentVersion ? (
              <span className="version-end">
                <Badge>Saat ini</Badge>
              </span>
            ) : (
              <button type="button" className="btn btn-secondary version-end" onClick={() => setOpen(v.version)} aria-label={`Lihat versi ${v.version}`}>
                Lihat
              </button>
            )}
          </li>
        ))}
      </ul>
      {open !== null ? <VersionDialog docId={doc.id} version={open} onClose={() => setOpen(null)} /> : null}
    </section>
  );
}

function VersionDialog({ docId, version, onClose }: { docId: string; version: number; onClose: () => void }) {
  const v = useAsync(() => API.getVersion(docId, version), [docId, version]);
  return (
    <Dialog
      open
      size="lg"
      title={`Versi ${version}`}
      description={v.data ? `${v.data.source === "ai" ? "Disusun Pluto" : "Diubah manual"} oleh ${v.data.createdByName}, ${formatDateTime(v.data.createdAt)}` : undefined}
      onClose={onClose}
      footer={
        <button type="button" className="btn btn-secondary" onClick={onClose}>
          Tutup
        </button>
      }
    >
      {v.loading ? (
        <SkeletonRows count={5} label="Memuat versi" />
      ) : v.error || !v.data ? (
        <Notice tone="danger" title="Versi belum dapat dimuat" action={<button type="button" className="btn btn-secondary" onClick={v.reload}>Muat ulang</button>}>
          <p>{API.errorMessage(v.error)}</p>
        </Notice>
      ) : (
        <Markdown source={v.data.content} className="doc-content" />
      )}
    </Dialog>
  );
}

function HistoryPanel({ events }: { events: API.DocumentEvent[] }) {
  const sorted = useMemo(() => [...events].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)), [events]);
  return (
    <section className="aside-section divided" aria-labelledby="history-title">
      <SectionHead id="history-title" title="Riwayat" />
      {sorted.length === 0 ? (
        <p className="quiet-line">Belum ada riwayat.</p>
      ) : (
        <ol className="history">
          {sorted.map((e) => (
            <li key={e.id} className="history-item">
              <p className="history-title">{EVENT_LABEL[e.action] ?? e.action}</p>
              <p className="row-meta">
                {e.actorName}, {formatDateTime(e.createdAt)}
                {e.fromStatus && e.toStatus && e.fromStatus !== e.toStatus ? `. ${STATUS_LABEL[e.fromStatus]} ke ${STATUS_LABEL[e.toStatus]}` : ""}
              </p>
              {e.note ? <p className="history-note pre-line">{e.note}</p> : null}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function CitationsPanel({ citations }: { citations: string[] }) {
  const kb = useAsync(() => (citations.length ? API.getKnowledge().catch(() => null) : Promise.resolve(null)), [citations.join("|")]);
  return (
    <section className="aside-section" aria-labelledby="cite-title">
      <SectionHead id="cite-title" title="Rujukan" lead={citations.length ? "Bagian basis pengetahuan yang dipakai Pluto." : undefined} />
      {citations.length === 0 ? (
        <p className="quiet-line">Tidak ada rujukan pengetahuan yang dipakai pada draf ini.</p>
      ) : (
        <ul className="citations">
          {citations.map((c) => {
            // The Library's title (or the id in words) and a readable section, never a raw slug.
            const { title, where, driveId } = citationLabel(c, kb.data?.files);
            const text = (
              <span className="citation-text">
                <span className="citation-title">{title}</span>
                {where ? <span className="citation-where">{where}</span> : null}
              </span>
            );
            return (
              <li key={c} className="citation" title={c}>
                <Glyph name="link" size={16} />
                {/* Same rule as the chat's Rujukan: a Drive-generated entry
                    opens its original document, a hand-written one cannot. */}
                {driveId ? (
                  <a className="citation-link" href={API.knowledgeFileUrl(driveId)} target="_blank" rel="noreferrer" title={`Buka dokumen: ${title}`}>
                    {text}
                  </a>
                ) : (
                  text
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
